import { ReceiptDetailRow } from '@/components/receipts/ReceiptDetailRow';
import type { TransactionItem } from '@/types/transactions';
import { formatTransactionNaira } from '@/utils/transactionFormatters';

interface GenericTransactionReceiptProps {
  transaction: TransactionItem;
  /** Admin-only: show profit, cost, and full metadata */
  showProfit?: boolean;
}

/** Only these metadata keys are safe to show on user receipts. */
const USER_META_ALLOWLIST: { key: string; label: string }[] = [
  { key: 'phoneNumber', label: 'Phone number' },
  { key: 'phone', label: 'Phone number' },
  { key: 'network', label: 'Network' },
  { key: 'planName', label: 'Plan' },
  { key: 'planType', label: 'Plan type' },
  { key: 'validity', label: 'Validity' },
  { key: 'meterNumber', label: 'Meter number' },
  { key: 'meterType', label: 'Meter type' },
  { key: 'customerName', label: 'Customer name' },
  { key: 'smartcardNumber', label: 'Smartcard number' },
  { key: 'billersCode', label: 'Smartcard / IUC' },
  { key: 'bouquet', label: 'Bouquet' },
  { key: 'subscriptionType', label: 'Subscription' },
  { key: 'candidateName', label: 'Candidate name' },
  { key: 'verificationValue', label: 'ID number' },
  { key: 'idNumber', label: 'ID number' },
  { key: 'verificationType', label: 'Verification type' },
  { key: 'verificationMethod', label: 'Verification method' },
  { key: 'slipType', label: 'Slip type' },
  { key: 'token', label: 'Token' },
  { key: 'units', label: 'Units' },
  { key: 'examName', label: 'Exam' },
  { key: 'purchasedCode', label: 'PIN / Code' },
  // Wallet funding — user-facing only (never sign / session / app secrets)
  { key: 'payerBankName', label: 'Sender bank' },
  { key: 'payerAccountName', label: 'Sender name' },
  { key: 'virtualAccountNo', label: 'Virtual account' },
  { key: 'orderNo', label: 'PalmPay order' },
];

function formatStatus(status: TransactionItem['status']) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatMetaValue(key: string, value: unknown): string {
  const raw = String(value).trim();
  if (!raw) return '';

  if (key === 'slipType' || key === 'verificationMethod' || key === 'verificationType' || key === 'meterType' || key === 'subscriptionType' || key === 'planType') {
    return raw.replace(/_/g, ' ');
  }

  if (key === 'virtualAccountNo' && raw.length >= 6) {
    return `${raw.slice(0, 3)}****${raw.slice(-4)}`;
  }

  if (key === 'payerAccountNo' && raw.length >= 6) {
    return `****${raw.slice(-4)}`;
  }

  return raw;
}

function userMetaRows(meta: Record<string, unknown>) {
  const rows: { label: string; value: string }[] = [];
  const seen = new Set<string>();

  for (const { key, label } of USER_META_ALLOWLIST) {
    if (seen.has(label)) continue;
    const value = meta[key];
    if (value == null || value === '' || typeof value === 'object') continue;
    const formatted = formatMetaValue(key, value);
    if (!formatted) continue;
    seen.add(label);
    rows.push({ label, value: formatted });
  }

  return rows;
}

function userFacingDescription(tx: TransactionItem): string | null {
  if (!tx.description) return null;
  // Prefer short human line; drop technical fee math if too long is fine for wallet
  const cleaned = tx.description.split('—')[0]?.trim() || tx.description;
  return cleaned;
}

export function GenericTransactionReceipt({ transaction, showProfit = false }: GenericTransactionReceiptProps) {
  const tx = transaction;
  const meta = tx.metadata ?? {};
  const extra = showProfit ? [] : userMetaRows(meta);
  const details = !showProfit ? userFacingDescription(tx) : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 border-b border-gray-100 pb-3">
        <p className="text-sm font-bold text-brand-navy">{tx.serviceName}</p>
        <p className="text-xs text-gray-500">{tx.categoryLabel}</p>
      </div>
      <ReceiptDetailRow label="Reference" value={tx.reference} mono />
      <ReceiptDetailRow label="Status" value={formatStatus(tx.status)} />
      <ReceiptDetailRow label="Amount" value={formatTransactionNaira(tx.amount)} />
      {details && <ReceiptDetailRow label="Details" value={details} />}
      {showProfit && tx.faceValue != null && (
        <ReceiptDetailRow label="Face value" value={formatTransactionNaira(tx.faceValue)} />
      )}
      {showProfit && tx.profit != null && (
        <ReceiptDetailRow label="Profit" value={formatTransactionNaira(tx.profit)} />
      )}
      {showProfit && tx.costPrice != null && (
        <ReceiptDetailRow label="Provider cost" value={formatTransactionNaira(tx.costPrice)} />
      )}
      {!showProfit && (
        <>
          <ReceiptDetailRow label="Balance before" value={formatTransactionNaira(tx.balanceBefore)} />
          <ReceiptDetailRow label="Balance after" value={formatTransactionNaira(tx.balanceAfter)} />
        </>
      )}
      {showProfit && tx.provider && <ReceiptDetailRow label="Provider" value={tx.provider} />}
      {extra.map((row) => (
        <ReceiptDetailRow key={row.label} label={row.label} value={row.value} />
      ))}
      <ReceiptDetailRow
        label="Date"
        value={new Date(tx.createdAt).toLocaleString('en-NG', {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      />
    </div>
  );
}
